<ng-template #membershipLine>
  <p class="renewal-card-picker__for" data-testid="payment-method-for">
    For <strong>{{ subject.planName }}</strong>
    @if (subject.plate) {
      · {{ subject.plate }}
    }
    @if (subject.nextRenewal) {
      · Next renewal {{ subject.nextRenewal | date: 'MMM d, y' }}
    }
    @if (subject.price !== null && subject.price !== '') {
      · {{ subject.price | currency: 'USD' }}
    }
  </p>
</ng-template>

<!-- Loading, a failed load, or why no card can be changed or saved: shown instead of the cards. -->
<ng-template #listState>
  @if (loading) {
    <p class="renewal-card-picker__status" aria-live="polite">Loading saved cards...</p>
  } @else if (loadError) {
    <div class="renewal-card-picker__problem" role="alert" data-testid="payment-method-load-error">
      <span>{{ loadError }}</span>
      <button type="button" class="renewal-card-picker__retry" (click)="load()">Try again</button>
    </div>
  } @else if (refusal) {
    <p class="renewal-card-picker__problem" role="status" data-testid="payment-method-refusal">
      {{ refusal }}
    </p>
  }
</ng-template>

<ng-template #cardSummary let-card>
  <span class="renewal-card-picker__badge" aria-hidden="true">{{ cardBadge(card) }}</span>
  <span class="renewal-card-picker__copy">
    <span class="renewal-card-picker__name">
      {{ cardLabel(card) }}
      @if (card.is_current) {
        <span class="renewal-card-picker__current">Current</span>
      }
    </span>
    <span class="renewal-card-picker__expiry">{{ cardExpiryLabel(card) }}</span>
  </span>
</ng-template>

<ng-template #addCardButton>
  <button
    type="button"
    class="renewal-card-picker__add"
    data-testid="payment-method-add-card"
    [disabled]="saving"
    (click)="openAddCard()"
  >
    <span aria-hidden="true">+</span> Add new card
  </button>
</ng-template>

@if (imported && step === 'select') {
  <app-confirm-action-modal
    title="Payment method"
    confirmLabel="Close"
    [showCancel]="false"
    [closeOnOverlayClick]="false"
    [mobileSheet]="false"
    testId="payment-method-modal"
    (confirm)="closed.emit()"
    (cancel)="closed.emit()"
  >
    <div class="renewal-card-picker">
      <ng-container *ngTemplateOutlet="membershipLine" />
      <p
        class="renewal-card-picker__billed-by"
        role="status"
        data-testid="payment-method-billed-by"
      >
        {{ importedNotice }}
      </p>

      @if (addedCardId && ready) {
        <p
          class="renewal-card-picker__notice"
          role="status"
          data-testid="payment-method-card-added"
        >
          {{ addedNotice }}
        </p>
      }

      @if (!ready) {
        <ng-container *ngTemplateOutlet="listState" />
      } @else {
        @if (cards.length > 0) {
          <ul
            class="renewal-card-picker__cards renewal-card-picker__cards--saved"
            aria-label="Saved cards"
          >
            @for (card of cards; track card.id) {
              <li
                class="renewal-card-picker__card renewal-card-picker__card--saved"
                [class.renewal-card-picker__card--expired]="card.is_expired"
                [attr.data-testid]="'payment-method-card-' + card.id"
              >
                <ng-container *ngTemplateOutlet="cardSummary; context: { $implicit: card }" />
              </li>
            }
          </ul>
        } @else {
          <p class="renewal-card-picker__status" data-testid="payment-method-empty">
            No cards saved for this membership yet.
          </p>
        }

        <ng-container *ngTemplateOutlet="addCardButton" />
      }
    </div>
  </app-confirm-action-modal>
} @else if (step === 'select') {
  <app-confirm-action-modal
    title="Payment method"
    message="Select the card to use for future membership renewals."
    confirmLabel="Save"
    busyLabel="Saving..."
    cancelButtonClass="btn-outlined"
    [busy]="saving"
    [confirmDisabled]="!canSave"
    [closeOnOverlayClick]="false"
    [mobileSheet]="false"
    testId="payment-method-modal"
    (confirm)="save()"
    (cancel)="closed.emit()"
  >
    <div class="renewal-card-picker">
      <ng-container *ngTemplateOutlet="membershipLine" />

      @if (addedCardId && ready) {
        <p
          class="renewal-card-picker__notice"
          role="status"
          data-testid="payment-method-card-added"
        >
          {{ addedNotice }}
        </p>
      } @else if (renewalCardInherited && ready) {
        <p class="renewal-card-picker__notice" role="status" data-testid="payment-method-inherited">
          This membership renews on the customer’s default card, which their other memberships can
          change. Save to keep it on this membership.
        </p>
      }

      @if (!ready) {
        <ng-container *ngTemplateOutlet="listState" />
      } @else {
        @if (cards.length > 0) {
          <div class="renewal-card-picker__cards" role="radiogroup" aria-label="Saved cards">
            @for (card of cards; track card.id) {
              <label
                class="renewal-card-picker__card"
                [class.renewal-card-picker__card--selected]="card.id === selectedId"
                [class.renewal-card-picker__card--expired]="card.is_expired"
                [attr.data-testid]="'payment-method-card-' + card.id"
              >
                <input
                  type="radio"
                  name="renewal-card"
                  class="renewal-card-picker__radio"
                  [value]="card.id"
                  [checked]="card.id === selectedId"
                  [disabled]="card.is_expired || saving"
                  (change)="select(card)"
                />
                <ng-container *ngTemplateOutlet="cardSummary; context: { $implicit: card }" />
              </label>
            }
          </div>
        } @else {
          <p class="renewal-card-picker__status" data-testid="payment-method-empty">
            No saved cards yet. Add a card for this membership to renew on.
          </p>
        }

        <ng-container *ngTemplateOutlet="addCardButton" />

        <p class="renewal-card-picker__note">
          <strong>Note:</strong> The selected card will be charged for the next renewal and all
          future renewals of this membership.
        </p>
      }

      @if (saveError) {
        <p class="renewal-card-picker__error" role="alert" data-testid="payment-method-save-error">
          {{ saveError }}
        </p>
      }
    </div>
  </app-confirm-action-modal>
} @else {
  <app-add-card-modal
    [subscriptionId]="subject.subscriptionId"
    [imported]="imported"
    (saved)="onCardAdded($event)"
    (back)="backFromAddCard()"
    (cancel)="closed.emit()"
  />
}
